/**
 * TargetRegistry - registro central de tudo que pode levar tiro (secao 4.11).
 *
 * Existe para que a BALISTICA nao precise conhecer a IA nem o jogador: quem pode
 * ser atingido se registra aqui com suas hitboxes, e a balistica so pergunta
 * "o que este raio acerta primeiro?".
 *
 * Estrategia do raycast (a ordem importa para o custo):
 *   1. BROADPHASE - esfera envolvente da entidade inteira (uma por entidade).
 *      Descarta quase todo mundo com um produto escalar e uma subtracao.
 *   2. NARROWPHASE - so quem passou: raio contra a caixa ORIENTADA (OBB) de cada
 *      hitbox. O teste e feito no espaco local do objeto (raio transformado pela
 *      inversa da matrixWorld), o que da precisao de caixa rotacionada sem
 *      precisar de Raycaster do three (que aloca array de intersecoes por chamada).
 *   3. Devolve sempre o acerto MAIS PROXIMO, nunca o primeiro encontrado.
 *
 * ZERO ALOCACAO por chamada: todos os vetores/matrizes sao temporarios de modulo
 * e os resultados saem de um pool circular.
 *
 * IMPORTANTE PARA QUEM CONSOME: o objeto devolvido por raycast() e REUTILIZADO
 * (pool de 8). Copie o que precisar (point/normal) antes de disparar outro
 * raycast. Nao guarde a referencia.
 */

import * as THREE from 'three';
import { HITBOX_MULTIPLIER } from '../core/Constants.js';

/** Partes validas vem de Constants - nao existe parte fora do multiplicador. */
const VALID_PARTS = Object.keys(HITBOX_MULTIPLIER);
const DEFAULT_PART = 'chest';

/**
 * Meia-aresta usada SO quando a hitbox nao e um Mesh com geometria (ex.: um
 * Object3D vazio marcando a posicao da cabeca). Caixa cubica porque nao da para
 * adivinhar a orientacao do corpo nesse caso.
 */
const DEFAULT_HALF_SIZE = { head: 0.115, chest: 0.20, stomach: 0.17, arm: 0.09, leg: 0.11 };

const HIT_POOL_SIZE = 8;

/**
 * Folga da esfera envolvente. As matrizes de mundo sao atualizadas no maximo a
 * cada poucos milissegundos (ver _refresh), entao a esfera precisa cobrir o
 * deslocamento de um passo fixo de um bot correndo (~6 m/s * 1/120 s = 5 cm).
 */
const BOUNDS_PAD = 0.12;

/** Intervalo maximo entre atualizacoes das matrizes/limites, em milissegundos. */
const REFRESH_INTERVAL_MS = 3;

// --- temporarios de modulo (zero alocacao no caminho quente) -----------------
const _rayOrigin = new THREE.Vector3();
const _rayDir = new THREE.Vector3();
const _oc = new THREE.Vector3();
const _localOrigin = new THREE.Vector3();
const _localDir = new THREE.Vector3();
const _localPoint = new THREE.Vector3();
const _worldPoint = new THREE.Vector3();
const _worldNormal = new THREE.Vector3();
const _bestPoint = new THREE.Vector3();
const _bestNormal = new THREE.Vector3();
const _boundsMin = new THREE.Vector3();
const _boundsMax = new THREE.Vector3();
const _scaleTmp = new THREE.Vector3();
const _invMatrix = new THREE.Matrix4();
const _normalMatrix = new THREE.Matrix3();

// Saida secundaria de rayBoxLocal (evita alocar objeto de retorno).
let _rbAxis = 0;   // 0=x 1=y 2=z ; -1 = origem dentro da caixa
let _rbSign = -1;  // face de entrada: -1 = face minima, +1 = face maxima

/**
 * Raio contra AABB no espaco LOCAL do objeto (o que, visto do mundo, e um OBB).
 * Devolve a distancia local de entrada, ou -1 se nao acerta.
 * Escreve a face de entrada em _rbAxis/_rbSign para o calculo da normal.
 */
function rayBoxLocal(ro, rd, min, max) {
  let tmin = 0;
  let tmax = Infinity;
  let axis = 0;
  let sign = -1;
  let entered = false;

  for (let i = 0; i < 3; i++) {
    const o = i === 0 ? ro.x : (i === 1 ? ro.y : ro.z);
    const d = i === 0 ? rd.x : (i === 1 ? rd.y : rd.z);
    const lo = i === 0 ? min.x : (i === 1 ? min.y : min.z);
    const hi = i === 0 ? max.x : (i === 1 ? max.y : max.z);

    // Raio paralelo ao slab: ou esta dentro dele para sempre, ou nunca acerta.
    // (tratar separado evita o NaN de 0 * Infinity quando o = lo exatamente)
    if (d > -1e-9 && d < 1e-9) {
      if (o < lo || o > hi) return -1;
      continue;
    }

    const inv = 1 / d;
    let t0 = (lo - o) * inv;
    let t1 = (hi - o) * inv;
    let s = -1;
    if (t0 > t1) { const swap = t0; t0 = t1; t1 = swap; s = 1; }

    if (t0 > tmin) { tmin = t0; axis = i; sign = s; entered = true; }
    if (t1 < tmax) tmax = t1;
    if (tmax < tmin) return -1;
  }

  if (tmax < 0) return -1;
  _rbAxis = entered ? axis : -1;
  _rbSign = sign;
  return tmin;
}

function isFiniteVec(v) {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function createHit() {
  return {
    entity: null,
    part: DEFAULT_PART,
    point: new THREE.Vector3(),
    normal: new THREE.Vector3(),
    distance: 0,
    // extras (nao fazem parte do contrato minimo, mas evitam consulta extra)
    team: -1,
    object: null,
    multiplier: 1
  };
}

/** Registro de uma hitbox: caixa local constante + cache do estado de mundo. */
function createHitbox(part, object) {
  const hb = {
    part,
    object,
    multiplier: HITBOX_MULTIPLIER[part] ?? 1,
    min: new THREE.Vector3(-0.2, -0.2, -0.2),
    max: new THREE.Vector3(0.2, 0.2, 0.2),
    localCenter: new THREE.Vector3(),
    localRadius: 0.35,
    worldCenter: new THREE.Vector3(),
    worldRadius: 0.35,
    valid: false
  };

  let box = null;
  const geo = object && object.geometry;
  if (geo) {
    if (!geo.boundingBox) {
      try { geo.computeBoundingBox(); } catch { /* geometria sem posicao - cai no padrao */ }
    }
    if (geo.boundingBox && isFiniteVec(geo.boundingBox.min) && isFiniteVec(geo.boundingBox.max)) {
      box = geo.boundingBox;
    }
  }

  if (box) {
    hb.min.copy(box.min);
    hb.max.copy(box.max);
  } else {
    const h = DEFAULT_HALF_SIZE[part] ?? 0.18;
    hb.min.set(-h, -h, -h);
    hb.max.set(h, h, h);
  }

  hb.localCenter.addVectors(hb.min, hb.max).multiplyScalar(0.5);
  const dx = hb.max.x - hb.min.x;
  const dy = hb.max.y - hb.min.y;
  const dz = hb.max.z - hb.min.z;
  hb.localRadius = 0.5 * Math.sqrt(dx * dx + dy * dy + dz * dz);
  return hb;
}

export class TargetRegistry {
  constructor(ctx) {
    this.ctx = ctx;

    /** @type {Map<object, object>} entidade -> entrada */
    this._entries = new Map();
    /** Lista paralela ao Map, para iterar por indice (sem alocar iterador). */
    this._list = [];
    /** Cache de all(): so e reconstruido em register/unregister. */
    this._entities = [];

    this._hits = new Array(HIT_POOL_SIZE);
    for (let i = 0; i < HIT_POOL_SIZE; i++) this._hits[i] = createHit();
    this._hitIndex = 0;

    this._frameStamp = -1;
    this._refreshedAt = -1e9;
    this._warned = false;
  }

  async init() { /* nada pesado: o registro nasce vazio e cresce com os spawns */ }

  get size() { return this._list.length; }

  count() { return this._list.length; }

  // --- registro ------------------------------------------------------------

  /**
   * Registra uma entidade atingivel.
   * @param entity qualquer objeto (Player, Bot, ...) usado como chave
   * @param {{hitboxes?:Array, team?:number, onDamage?:Function, object?:THREE.Object3D}} data
   */
  register(entity, data) {
    if (!entity) return null;
    const opts = data || {};

    const entry = this._entries.get(entity) || {
      entity,
      team: -1,
      onDamage: null,
      boxes: [],
      center: new THREE.Vector3(),
      radius: 0,
      active: true
    };

    entry.team = typeof opts.team === 'number' ? opts.team : -1;
    entry.onDamage = typeof opts.onDamage === 'function' ? opts.onDamage : null;
    entry.active = opts.active !== false;
    entry.boxes.length = 0;

    const list = opts.hitboxes;
    if (Array.isArray(list)) {
      for (let i = 0; i < list.length; i++) {
        const h = list[i];
        if (!h) continue;
        const object = h.object || h.mesh || h.node || null;
        if (!object) continue;
        const part = VALID_PARTS.indexOf(h.part) >= 0 ? h.part : DEFAULT_PART;
        entry.boxes.push(createHitbox(part, object));
      }
    } else if (list && typeof list === 'object') {
      // Tolerancia: aceita tambem o formato de mapa { head: obj, chest: obj }.
      for (const part of VALID_PARTS) {
        const object = list[part];
        if (object) entry.boxes.push(createHitbox(part, object));
      }
    }

    // Ultimo recurso: entidade sem hitbox declarada mas com um corpo visivel.
    // Melhor um alvo grosseiro do que um alvo invulneravel.
    if (entry.boxes.length === 0) {
      const fallback = opts.object || opts.root || entity.object3D || entity.root || entity.mesh || null;
      if (fallback && fallback.isObject3D) entry.boxes.push(createHitbox(DEFAULT_PART, fallback));
    }

    if (!this._entries.has(entity)) {
      this._entries.set(entity, entry);
      this._list.push(entry);
      this._entities.push(entity);
    }

    entry.radius = 0;
    this._frameStamp = -1;      // forca recalculo dos limites no proximo raycast
    this._refreshedAt = -1e9;
    return entry;
  }

  unregister(entity) {
    const entry = this._entries.get(entity);
    if (!entry) return false;
    this._entries.delete(entity);

    const i = this._list.indexOf(entry);
    if (i >= 0) this._list.splice(i, 1);
    const j = this._entities.indexOf(entity);
    if (j >= 0) this._entities.splice(j, 1);
    return true;
  }

  /** Entrada crua (team, onDamage, hitboxes). Usado pela balistica ao aplicar dano. */
  get(entity) {
    return this._entries.get(entity) || null;
  }

  has(entity) {
    return this._entries.has(entity);
  }

  teamOf(entity) {
    const e = this._entries.get(entity);
    return e ? e.team : -1;
  }

  /** Liga/desliga a entidade sem perder o registro (morte, respawn, invulneravel). */
  setActive(entity, active) {
    const e = this._entries.get(entity);
    if (e) e.active = !!active;
  }

  /**
   * Lista de entidades registradas. O array e INTERNO e reaproveitado - trate
   * como somente leitura (copie antes de reordenar/filtrar destrutivamente).
   */
  all() {
    return this._entities;
  }

  clear() {
    this._entries.clear();
    this._list.length = 0;
    this._entities.length = 0;
  }

  // --- consulta ------------------------------------------------------------

  /**
   * Acerto mais proximo do raio contra as hitboxes registradas.
   * @param {THREE.Vector3} origin
   * @param {THREE.Vector3} dir  (nao precisa estar normalizado)
   * @param {number} maxDistance
   * @param {number} [excludeTeam] time que NAO pode ser atingido (fogo amigo)
   * @returns {null|{entity, part, point, normal, distance, team, object, multiplier}}
   */
  raycast(origin, dir, maxDistance = 200, excludeTeam = null) {
    const n = this._list.length;
    if (n === 0 || !origin || !dir) return null;

    _rayOrigin.set(origin.x, origin.y, origin.z);
    _rayDir.set(dir.x, dir.y, dir.z);
    if (!isFiniteVec(_rayOrigin) || !isFiniteVec(_rayDir)) return null;

    const dirLen = _rayDir.length();
    if (dirLen < 1e-8) return null;
    _rayDir.multiplyScalar(1 / dirLen);

    let best = Number.isFinite(maxDistance) && maxDistance > 0 ? maxDistance : 200;
    const exclude = typeof excludeTeam === 'number' ? excludeTeam : null;

    this._refresh();

    let bestEntry = null;
    let bestBox = null;

    for (let i = 0; i < n; i++) {
      const entry = this._list[i];
      if (!entry.active || entry.radius <= 0) continue;
      if (exclude !== null && entry.team === exclude) continue;
      if (entry.entity.alive === false) continue;   // cadaver nao para bala

      // --- broadphase: raio contra esfera envolvente da entidade ---
      _oc.subVectors(entry.center, _rayOrigin);
      const r = entry.radius;
      const tca = _oc.dot(_rayDir);
      if (tca < -r) continue;              // esfera inteira atras do atirador
      if (tca - r > best) continue;        // ja existe acerto mais perto
      const d2 = _oc.lengthSq() - tca * tca;
      if (d2 > r * r) continue;            // raio passa ao lado

      // --- narrowphase: raio contra cada hitbox, no espaco local dela ---
      const boxes = entry.boxes;
      for (let b = 0; b < boxes.length; b++) {
        const hb = boxes[b];
        if (!hb.valid) continue;

        const m = hb.object.matrixWorld;
        _invMatrix.copy(m).invert();
        _localOrigin.copy(_rayOrigin).applyMatrix4(_invMatrix);
        _localDir.copy(_rayDir).transformDirection(_invMatrix);

        const tLocal = rayBoxLocal(_localOrigin, _localDir, hb.min, hb.max);
        if (tLocal < 0) continue;

        // Distancia medida no MUNDO (o espaco local pode ter escala diferente).
        _localPoint.copy(_localDir).multiplyScalar(tLocal).add(_localOrigin);
        _worldPoint.copy(_localPoint).applyMatrix4(m);
        const dist = _worldPoint.distanceTo(_rayOrigin);
        if (!(dist >= 0) || dist > best) continue;

        if (_rbAxis < 0) {
          // Origem dentro da hitbox (tiro encostado): normal aponta de volta.
          _worldNormal.copy(_rayDir).multiplyScalar(-1);
        } else {
          _worldNormal.set(0, 0, 0);
          _worldNormal.setComponent(_rbAxis, _rbSign);
          _normalMatrix.getNormalMatrix(m);
          _worldNormal.applyMatrix3(_normalMatrix);
          const nl = _worldNormal.length();
          if (nl > 1e-8) _worldNormal.multiplyScalar(1 / nl);
          else _worldNormal.copy(_rayDir).multiplyScalar(-1);
        }

        best = dist;
        bestEntry = entry;
        bestBox = hb;
        _bestPoint.copy(_worldPoint);
        _bestNormal.copy(_worldNormal);
      }
    }

    if (!bestEntry || !bestBox) return null;

    const out = this._hits[this._hitIndex];
    this._hitIndex = (this._hitIndex + 1) % HIT_POOL_SIZE;
    out.entity = bestEntry.entity;
    out.team = bestEntry.team;
    out.part = bestBox.part;
    out.multiplier = bestBox.multiplier;
    out.object = bestBox.object;
    out.point.copy(_bestPoint);
    out.normal.copy(_bestNormal);
    out.distance = best;
    return out;
  }

  /**
   * Entidade viva mais proxima de uma posicao.
   * @param {THREE.Vector3} position
   * @param {number} [team] filtra POR time (quem pertence a este time). null = qualquer um.
   * @param {number} [maxDist]
   */
  nearest(position, team = null, maxDist = Infinity) {
    if (!position) return null;
    const n = this._list.length;
    if (n === 0) return null;

    this._refresh();

    const wantTeam = typeof team === 'number' ? team : null;
    let bestD2 = Number.isFinite(maxDist) ? maxDist * maxDist : Infinity;
    let best = null;

    for (let i = 0; i < n; i++) {
      const entry = this._list[i];
      if (!entry.active || entry.radius <= 0) continue;
      if (wantTeam !== null && entry.team !== wantTeam) continue;
      if (entry.entity.alive === false) continue;

      const dx = entry.center.x - position.x;
      const dy = entry.center.y - position.y;
      const dz = entry.center.z - position.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = entry.entity; }
    }
    return best;
  }

  /** Centro (mundo) da esfera envolvente de uma entidade, escrito em `target`. */
  getCenter(entity, target) {
    const entry = this._entries.get(entity);
    if (!entry || !target) return null;
    this._refresh();
    target.copy(entry.center);
    return target;
  }

  // --- interno -------------------------------------------------------------

  /**
   * Atualiza matrizes de mundo das hitboxes e os limites por entidade.
   *
   * Custa pouco e roda no maximo uma vez por frame (ou a cada poucos ms, para
   * cobrir varios passos fixos dentro do mesmo frame - bot que anda entre dois
   * fixedUpdate nao pode ficar com hitbox atrasada). O three so atualiza
   * matrixWorld na hora de renderizar; sem isto o tiro acertaria a posicao do
   * frame anterior.
   */
  _refresh() {
    const engineFrame = this.ctx && this.ctx.engine ? this.ctx.engine.frame : null;
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;

    if (typeof engineFrame === 'number') {
      if (engineFrame === this._frameStamp && (now - this._refreshedAt) < REFRESH_INTERVAL_MS) return;
      this._frameStamp = engineFrame;
    } else if ((now - this._refreshedAt) < REFRESH_INTERVAL_MS) {
      return;
    }
    this._refreshedAt = now;

    const list = this._list;
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      if (!entry.active) { entry.radius = 0; continue; }
      try {
        this._refreshEntry(entry);
      } catch (err) {
        entry.radius = 0;
        if (!this._warned) {
          this._warned = true;
          console.warn('[Targets] falha ao atualizar hitboxes de uma entidade:', err);
        }
      }
    }
  }

  _refreshEntry(entry) {
    const boxes = entry.boxes;
    let used = 0;

    _boundsMin.set(Infinity, Infinity, Infinity);
    _boundsMax.set(-Infinity, -Infinity, -Infinity);

    for (let b = 0; b < boxes.length; b++) {
      const hb = boxes[b];
      const obj = hb.object;
      hb.valid = false;
      if (!obj) continue;

      obj.updateWorldMatrix(true, false);
      const m = obj.matrixWorld;
      hb.worldCenter.copy(hb.localCenter).applyMatrix4(m);
      if (!isFiniteVec(hb.worldCenter)) continue;

      // Maior fator de escala do objeto (colunas da matriz), para inflar o raio.
      const sx = _scaleTmp.setFromMatrixColumn(m, 0).length();
      const sy = _scaleTmp.setFromMatrixColumn(m, 1).length();
      const sz = _scaleTmp.setFromMatrixColumn(m, 2).length();
      const s = Math.max(sx, Math.max(sy, sz));
      hb.worldRadius = hb.localRadius * (Number.isFinite(s) && s > 0 ? s : 1);
      hb.valid = true;
      used++;

      if (hb.worldCenter.x - hb.worldRadius < _boundsMin.x) _boundsMin.x = hb.worldCenter.x - hb.worldRadius;
      if (hb.worldCenter.y - hb.worldRadius < _boundsMin.y) _boundsMin.y = hb.worldCenter.y - hb.worldRadius;
      if (hb.worldCenter.z - hb.worldRadius < _boundsMin.z) _boundsMin.z = hb.worldCenter.z - hb.worldRadius;
      if (hb.worldCenter.x + hb.worldRadius > _boundsMax.x) _boundsMax.x = hb.worldCenter.x + hb.worldRadius;
      if (hb.worldCenter.y + hb.worldRadius > _boundsMax.y) _boundsMax.y = hb.worldCenter.y + hb.worldRadius;
      if (hb.worldCenter.z + hb.worldRadius > _boundsMax.z) _boundsMax.z = hb.worldCenter.z + hb.worldRadius;
    }

    if (used === 0) { entry.radius = 0; return; }

    entry.center.addVectors(_boundsMin, _boundsMax).multiplyScalar(0.5);

    // Raio justo: maior distancia do centro ate a borda de uma hitbox.
    let r = 0;
    for (let b = 0; b < boxes.length; b++) {
      const hb = boxes[b];
      if (!hb.valid) continue;
      const d = entry.center.distanceTo(hb.worldCenter) + hb.worldRadius;
      if (d > r) r = d;
    }
    entry.radius = r + BOUNDS_PAD;
  }

  dispose() {
    this.clear();
  }
}
